import { useEffect, useMemo, useState } from "react";
import useUser from "../../customHooks/useUser";
import { AdditionalUserData, ChoiceEmployee } from "../../globals/interfaces";
import apiClient from "../../apiClient";
import useSWR from "swr";
import _, { map } from "lodash";
import { Label } from "reactstrap";
import { Input } from "reactstrap";
import styled from "styled-components";
import ActionIcon from "../custom/ActionIcon";
import { getTsDate, notify } from "../../utils";
import useLoading from "../../customHooks/useLoading";
import Select from "../Select";
import { slaOptions } from "./constants";

type Mapping = Record<string, {
    label: string,
    value: string
}>;

type UpdateMapping = (key: string, value: string) => void;

const fallbackData = {
    schema: {},
    userData: {}
}

const fetcher = async (key: string): Promise<{
    schema: AdditionalUserData,
    userData: AdditionalUserData
}> => {
    try {
        const res = await apiClient.get(key);
        return res.data;
    } catch (err) {
        alert(err);
        return fallbackData;
    }
}

export default function AdditionalEmployeeData({
    selectedEmployee,
    triggerAdpSync,
    toggle
}: {
    selectedEmployee: ChoiceEmployee;
    triggerAdpSync: number;
    toggle: () => void;
}) {
    const { user } = useUser();
    const {
        data,
        mutate
    } = useSWR(`/additional-user-data/schema-and-user-data?unit=${user.s_unit}&email=${selectedEmployee.s_email}`, fetcher, {
        fallbackData
    });


    const { setLoading } = useLoading();
    const { schema, userData = {} } = data;

    // State to keep the original user data
    const [originalUserData, setOriginalUserData] = useState({});

    // Update the originalUserData when the data is initially fetched
    useEffect(() => {
        if (!_.isEmpty(userData) && _.isEmpty(originalUserData)) {
            setOriginalUserData(userData);
        }
    }, [userData, originalUserData]);

    useEffect(() => {
        mutate();
    }, [triggerAdpSync]);

    const mapping = useMemo(() => {
        const map: Mapping = {};
        for (const key in schema) {
            map[key] = {
                label: schema[key],
                value: userData[key]
            }
        }
        return map;
    }, [schema, userData]);

    const updateMapping: UpdateMapping = (key, value) => {
        const copy = _.cloneDeep(userData);
        copy[key] = value;
        mutate({
            schema,
            userData: copy
        }, false);
    }

    const generateEmployeeLog = (initialValues, currentValues) => {
        // Start building the changes string
        let changesString = 'Changes made to the following fields - ';
        let changes = false;

        // Iterate over each field
        for (let fieldName in initialValues) {
            // If the field's value has been changed
            if (initialValues[fieldName] !== currentValues[fieldName]) {
                changes = true;
                // Add to the changes string
                const fieldAlias = schema[fieldName] || fieldName;
                const newValue = currentValues[fieldName] || ''.toUpperCase();
                changesString += `${fieldAlias}: ${initialValues[fieldName]} -> ${newValue}; `;
            }
        }

        // Trim the trailing comma and space
        if (changesString.endsWith(', ')) {
            changesString = changesString.substring(0, changesString.length - 2);
        }

        return changes ? changesString : null;
    }


    const updateUserData = async () => {
        try {
            setLoading(true);
            const employeeLog = generateEmployeeLog(originalUserData, userData);
            const res = await apiClient.post(`/additional-user-data/user`, {
                unit: user.s_unit,
                email: selectedEmployee.s_email,
                additionalFields: userData,
                employee_id: selectedEmployee.id,
                s_modified_by: user.s_email,
                t_modified: getTsDate(),
                employeeLog
            });
            notify('Updated');
            toggle();
        } catch (err) {
            alert(err);
        } finally {
            setLoading(false);
        }
    }
    
    return (
        <Container>
            <ol>
                {
                    Object.keys(mapping).map(key => (
                        <RenderItem 
                            mapping={mapping}
                            currentKey={key}
                            updateMapping={updateMapping}
                            key={key}
                        />
                    ))
                }
            </ol>
            <CustomSaveContainer onClick={() => updateUserData()}>
                <ActionIcon
                    type={'save'}
                    onClick={() => { }}
                />
            </CustomSaveContainer>
        </Container>
    );
}

const RenderItem = ({
    mapping,
    currentKey,
    updateMapping
}: {
    mapping: Mapping;
    currentKey: string;
    updateMapping: UpdateMapping;
}) => {

    if (!mapping[currentKey]) return null;

    const FormElement = currentKey === 'slaPosition' ?
        <Select 
            options={slaOptions}
            value={mapping[currentKey].value}
            onChange={option => updateMapping(currentKey, String(option.value))}
        /> : 
        <Input type={'text'} value={mapping[currentKey].value} onChange={(e) => updateMapping(currentKey, e.target.value)} />;

    return (
        <CustomListItem>
            <Label>{mapping[currentKey].label}</Label>
            {FormElement}
        </CustomListItem>
    );
}   

const Container = styled.div`
    
`;

const CustomListItem = styled.li`
    margin-bottom: 10px;
`;

const CustomSaveContainer = styled.div`
    position: absolute;
    right: 5%;
    bottom: -75px;;
    /* height: 50px;
    width: 50px; */
    z-index: 10;

    &:hover {
        cursor: pointer;
    }
`;